import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Space,
  Popconfirm,
  message,
  Card,
  Typography,
  Tooltip,
  Tag,
  Row,
  Col,
  Statistic,
  Select,
  DatePicker,
  Input,
  Descriptions,
  Divider,
  Alert,
  Empty,
  Avatar,
  Spin,
} from 'antd';
import {
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  FilterOutlined,
  ClearOutlined,
  DownloadOutlined,
  ClockCircleOutlined,
  UserOutlined,
  AuditOutlined,
  DatabaseOutlined,
  HistoryOutlined,
  ExclamationCircleOutlined,
  FileOutlined,
  GlobalOutlined,
  ThunderboltOutlined,
  PieChartOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import PagesWrapper from '../../../PagesWrapper';
import dataDeletionLogsService from '../../../services/dataDeletionLogsService';
import { getFileUrl } from '../../../utils/urlUtils';
import styles from './DataDeletionLogs.module.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * Entity type icons
 */
const EntityTypeIcons = {
  FileEntity: <FileOutlined />,
  Country: <GlobalOutlined />,
  LevelOfDifficulty: <ThunderboltOutlined />,
  ApplicationUser: <UserOutlined />,
  Course: <DatabaseOutlined />,
  default: <DatabaseOutlined />,
};

/**
 * Data Deletion Logs Management Page
 * Admin page for viewing and managing data deletion audit logs
 */
const DataDeletionLogs = () => {
  const { t } = useTranslation();
  
  // Data state
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [entityTypes, setEntityTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  
  // Filters
  const [filters, setFilters] = useState({
    entityType: null,
    search: '',
    dateRange: null,
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Modals
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [cleanupModalVisible, setCleanupModalVisible] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupDate, setCleanupDate] = useState(null);
  const [cleanupEntityType, setCleanupEntityType] = useState(null);

  /**
   * Fetch logs from API
   */
  const fetchLogs = useCallback(async (page = 1, pageSize = 50) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize,
        sortBy: 'DeletedAt',
        sortDescending: true,
      };
      
      if (filters.entityType) params.entityType = filters.entityType;
      if (filters.search) params.search = filters.search;
      if (filters.dateRange && filters.dateRange[0]) {
        params.fromDate = filters.dateRange[0].toISOString();
      }
      if (filters.dateRange && filters.dateRange[1]) {
        params.toDate = filters.dateRange[1].toISOString();
      }

      const response = await dataDeletionLogsService.getLogs(params);
      setLogs(response.items || []);
      setPagination({
        current: response.page,
        pageSize: response.pageSize,
        total: response.totalCount,
      });
    } catch (error) {
      message.error(t('admin.deletionLogs.fetchError', 'Failed to load deletion logs'));
      console.error('Failed to fetch deletion logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  /**
   * Fetch statistics
   */
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await dataDeletionLogsService.getStats();
      setStats(response);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  /**
   * Fetch entity types
   */
  const fetchEntityTypes = useCallback(async () => {
    try {
      const types = await dataDeletionLogsService.getEntityTypes();
      setEntityTypes(types || []);
    } catch (error) {
      console.error('Failed to fetch entity types:', error);
    }
  }, []);

  useEffect(() => {
    fetchLogs(pagination.current, pagination.pageSize);
    fetchStats();
    fetchEntityTypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handle table pagination changes
   */
  const handleTableChange = (newPagination, tableFilters, sorter) => {
    fetchLogs(newPagination.current, newPagination.pageSize);
  };

  /**
   * Apply filters
   */
  const applyFilters = () => {
    fetchLogs(1, pagination.pageSize);
  };

  /**
   * Reset filters
   */
  const resetFilters = () => {
    setFilters({
      entityType: null,
      search: '',
      dateRange: null,
    });
    setTimeout(() => fetchLogs(1, pagination.pageSize), 0);
  };

  /**
   * View log detail
   */
  const viewLogDetail = async (log) => {
    try {
      const fullLog = await dataDeletionLogsService.getLog(log.id);
      setSelectedLog(fullLog);
      setDetailModalVisible(true);
    } catch (error) {
      message.error(t('admin.deletionLogs.detailError', 'Failed to load log details'));
    }
  };

  /**
   * Delete single log
   */
  const handleDelete = async (id) => {
    try {
      await dataDeletionLogsService.deleteLog(id);
      message.success(t('admin.deletionLogs.deleteSuccess', 'Deletion log removed'));
      fetchLogs(pagination.current, pagination.pageSize);
      fetchStats();
    } catch (error) {
      message.error(t('admin.deletionLogs.deleteError', 'Failed to delete log entry'));
    }
  };

  /**
   * Cleanup old logs
   */
  const handleCleanup = async () => {
    if (!cleanupDate) {
      message.warning('Please select a date');
      return;
    }
    
    setCleanupLoading(true);
    try {
      const result = await dataDeletionLogsService.cleanupLogs({
        olderThan: cleanupDate.toISOString(),
        entityType: cleanupEntityType,
      });
      message.success(result.message || `Cleaned up ${result.deletedCount} logs`);
      setCleanupModalVisible(false);
      setCleanupDate(null);
      setCleanupEntityType(null);
      fetchLogs(1, pagination.pageSize);
      fetchStats();
    } catch (error) {
      message.error(t('admin.deletionLogs.cleanupError', 'Cleanup failed'));
    } finally {
      setCleanupLoading(false);
    }
  };

  /**
   * Export logs
   */
  const handleExport = async () => {
    try {
      const fromDate = filters.dateRange?.[0] || dayjs().subtract(30, 'days');
      const toDate = filters.dateRange?.[1] || dayjs();
      
      const logs = await dataDeletionLogsService.exportLogs({
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        entityType: filters.entityType,
      });
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `deletion-logs-${fromDate.format('YYYY-MM-DD')}-to-${toDate.format('YYYY-MM-DD')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success(`Exported ${logs.length} logs`);
    } catch (error) {
      message.error(t('admin.deletionLogs.exportError', 'Export failed'));
    }
  };

  /**
   * Get entity type class
   */
  const getEntityTypeClass = (entityType) => {
    switch (entityType) {
      case 'FileEntity': return styles.entityTypeFileEntity;
      case 'Country': return styles.entityTypeCountry;
      case 'LevelOfDifficulty': return styles.entityTypeLevelOfDifficulty;
      case 'ApplicationUser': return styles.entityTypeApplicationUser;
      case 'Course': return styles.entityTypeCourse;
      default: return styles.entityTypeDefault;
    }
  };

  /**
   * Format JSON for display
   */
  const formatJson = (data) => {
    if (!data) return null;
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  /**
   * Table columns configuration
   */
  const columns = [
    {
      title: t('admin.deletionLogs.columns.entityType', 'Entity Type'),
      dataIndex: 'entityType',
      key: 'entityType',
      width: 150,
      render: (entityType) => (
        <span className={`${styles.entityTypeBadge} ${getEntityTypeClass(entityType)}`}>
          {EntityTypeIcons[entityType] || EntityTypeIcons.default}
          <span style={{ marginLeft: 4 }}>{entityType}</span>
        </span>
      ),
      filters: entityTypes.map(type => ({ text: type, value: type })),
      onFilter: (value, record) => record.entityType === value,
    },
    {
      title: t('admin.deletionLogs.columns.description', 'Description'),
      dataIndex: 'entityDescription',
      key: 'entityDescription',
      ellipsis: true,
      render: (description, record) => (
        <Tooltip title={description}>
          <span className={styles.descriptionCell}>
            <Text strong>#{record.entityId}</Text>
            <Text style={{ marginLeft: 8 }}>{description}</Text>
          </span>
        </Tooltip>
      ),
    },
    {
      title: t('admin.deletionLogs.columns.deletedAt', 'Deleted'),
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      width: 150,
      render: (date) => (
        <Tooltip title={dataDeletionLogsService.formatTimestamp(date)}>
          <span>{dataDeletionLogsService.getRelativeTime(date)}</span>
        </Tooltip>
      ),
      sorter: (a, b) => new Date(a.deletedAt) - new Date(b.deletedAt),
      defaultSortOrder: 'descend',
    },
    {
      title: t('admin.deletionLogs.columns.deletedBy', 'Deleted By'),
      key: 'deletedBy',
      width: 180,
      render: (_, record) => record.deletedByUserEmail ? (
        <Space>
          <Avatar 
            size="small" 
            src={getFileUrl(record.deletedByUserProfileImageUrl)}
            icon={<UserOutlined />} 
          />
          <span>{record.deletedByUserDisplayName || record.deletedByUserEmail}</span>
        </Space>
      ) : (
        <Text type="secondary">System</Text>
      ),
    },
    {
      title: t('admin.deletionLogs.columns.reason', 'Reason'),
      dataIndex: 'deletionReason',
      key: 'reason',
      width: 150,
      ellipsis: true,
      render: (reason) => reason || <Text type="secondary">-</Text>,
    },
    {
      title: t('admin.deletionLogs.columns.actions', 'Actions'),
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('common.view', 'View')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => viewLogDetail(record)}
            />
          </Tooltip>
          <Popconfirm
            title={t('admin.deletionLogs.deleteConfirm', 'Remove this audit log entry?')}
            onConfirm={() => handleDelete(record.id)}
          >
            <Tooltip title={t('common.delete', 'Delete')}>
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /**
   * Render statistics cards
   */
  const renderStatistics = () => (
    <Row gutter={[16, 16]} className={styles.statsRow}>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={statsLoading}>
          <Statistic
            title={t('admin.deletionLogs.stats.total', 'Total Logs')}
            value={stats?.totalLogs || 0}
            prefix={<AuditOutlined style={{ color: '#722ed1' }} />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={statsLoading}>
          <Statistic
            title={t('admin.deletionLogs.stats.last24h', 'Last 24 Hours')}
            value={stats?.logsLast24Hours || 0}
            prefix={<ClockCircleOutlined style={{ color: '#1890ff' }} />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={statsLoading}>
          <Statistic
            title={t('admin.deletionLogs.stats.last7d', 'Last 7 Days')}
            value={stats?.logsLast7Days || 0}
            prefix={<CalendarOutlined style={{ color: '#52c41a' }} />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={statsLoading}>
          <Statistic
            title={t('admin.deletionLogs.stats.last30d', 'Last 30 Days')}
            value={stats?.logsLast30Days || 0}
            prefix={<HistoryOutlined style={{ color: '#faad14' }} />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={statsLoading}>
          <Statistic
            title={t('admin.deletionLogs.stats.entityTypes', 'Entity Types')}
            value={Object.keys(stats?.byEntityType || {}).length}
            prefix={<DatabaseOutlined style={{ color: '#eb2f96' }} />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={statsLoading}>
          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">Time Span</Text>
            <div style={{ marginTop: 4 }}>
              {stats?.oldestLog ? (
                <Text strong>
                  {dayjs(stats.oldestLog).format('MMM D, YYYY')}
                </Text>
              ) : (
                <Text type="secondary">-</Text>
              )}
              <Text type="secondary"> to </Text>
              {stats?.newestLog ? (
                <Text strong>
                  {dayjs(stats.newestLog).format('MMM D, YYYY')}
                </Text>
              ) : (
                <Text type="secondary">-</Text>
              )}
            </div>
          </div>
        </Card>
      </Col>
    </Row>
  );

  /**
   * Render entity type distribution
   */
  const renderEntityDistribution = () => {
    if (!stats?.byEntityType) return null;
    
    const entityTypeCounts = stats.byEntityType;
    const total = Object.values(entityTypeCounts).reduce((a, b) => a + b, 0);
    if (total === 0) return null;
    
    return (
      <Card 
        title={<Space><PieChartOutlined /> Entity Type Distribution</Space>} 
        size="small"
        style={{ marginBottom: 16 }}
      >
        <div>
          {Object.entries(entityTypeCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([entityType, count]) => (
              <div key={entityType} className={styles.entityTypeItem}>
                <div className={styles.entityTypeLabel}>
                  {EntityTypeIcons[entityType] || EntityTypeIcons.default}
                  <span style={{ marginLeft: 8 }}>{entityType}</span>
                </div>
                <div className={styles.entityTypeBar}>
                  <div
                    className={styles.entityTypeBarFill}
                    style={{
                      width: `${(count / total) * 100}%`,
                      backgroundColor: dataDeletionLogsService.getEntityTypeColor(entityType),
                    }}
                  />
                </div>
                <Text style={{ minWidth: 80, textAlign: 'right' }}>
                  {count} ({Math.round((count / total) * 100)}%)
                </Text>
              </div>
            ))}
        </div>
      </Card>
    );
  };

  /**
   * Render filter panel
   */
  const renderFilterPanel = () => (
    <Card className={styles.filterPanel} size="small">
      <Row gutter={[16, 16]} align="bottom">
        <Col xs={24} sm={12} md={6}>
          <div className={styles.filterLabel}>
            {t('admin.deletionLogs.filters.entityType', 'Entity Type')}
          </div>
          <Select
            style={{ width: '100%' }}
            placeholder={t('admin.deletionLogs.filters.allTypes', 'All Types')}
            allowClear
            value={filters.entityType}
            onChange={(value) => setFilters({ ...filters, entityType: value })}
          >
            {entityTypes.map(type => (
              <Select.Option key={type} value={type}>
                <Space>
                  {EntityTypeIcons[type] || EntityTypeIcons.default}
                  {type}
                </Space>
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div className={styles.filterLabel}>
            {t('admin.deletionLogs.filters.dateRange', 'Date Range')}
          </div>
          <RangePicker
            style={{ width: '100%' }}
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div className={styles.filterLabel}>
            {t('admin.deletionLogs.filters.search', 'Search')}
          </div>
          <Input
            placeholder={t('admin.deletionLogs.filters.searchPlaceholder', 'Search description, email...')}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Space>
            <Button type="primary" onClick={applyFilters} icon={<SearchOutlined />}>
              {t('common.apply', 'Apply')}
            </Button>
            <Button onClick={resetFilters} icon={<ClearOutlined />}>
              {t('common.reset', 'Reset')}
            </Button>
          </Space>
        </Col>
      </Row>
      
      <Divider style={{ margin: '12px 0' }} />
      
      <div className={styles.timeRangeButtons}>
        <Text type="secondary" style={{ marginRight: 8 }}>
          {t('admin.deletionLogs.filters.quickRange', 'Quick Range:')}
        </Text>
        <Button size="small" onClick={() => setFilters({ ...filters, dateRange: [dayjs().subtract(24, 'hours'), dayjs()] })}>
          Last 24 Hours
        </Button>
        <Button size="small" onClick={() => setFilters({ ...filters, dateRange: [dayjs().subtract(7, 'days'), dayjs()] })}>
          Last 7 Days
        </Button>
        <Button size="small" onClick={() => setFilters({ ...filters, dateRange: [dayjs().subtract(30, 'days'), dayjs()] })}>
          Last 30 Days
        </Button>
        <Button size="small" onClick={() => setFilters({ ...filters, dateRange: [dayjs().subtract(90, 'days'), dayjs()] })}>
          Last 90 Days
        </Button>
      </div>
    </Card>
  );

  /**
   * Render detail modal
   */
  const renderDetailModal = () => (
    <Modal
      title={
        <Space>
          <AuditOutlined style={{ color: '#722ed1' }} />
          {t('admin.deletionLogs.detail.title', 'Deletion Log Details')}
        </Space>
      }
      open={detailModalVisible}
      onCancel={() => {
        setDetailModalVisible(false);
        setSelectedLog(null);
      }}
      footer={[
        <Button key="close" onClick={() => setDetailModalVisible(false)}>
          {t('common.close', 'Close')}
        </Button>,
        <Popconfirm
          key="delete"
          title={t('admin.deletionLogs.deleteConfirm', 'Remove this audit log entry?')}
          onConfirm={() => {
            handleDelete(selectedLog.id);
            setDetailModalVisible(false);
          }}
        >
          <Button danger icon={<DeleteOutlined />}>
            {t('common.delete', 'Delete')}
          </Button>
        </Popconfirm>,
      ]}
      width={800}
    >
      {selectedLog && (
        <div>
          {/* Entity Preview */}
          <div className={styles.entityPreview}>
            <span className={styles.entityIcon}>
              {EntityTypeIcons[selectedLog.entityType] || EntityTypeIcons.default}
            </span>
            <div className={styles.entityInfo}>
              <div className={styles.entityDescription}>
                {selectedLog.entityDescription}
              </div>
              <div className={styles.entityMeta}>
                <Space split={<Divider type="vertical" />}>
                  <span className={`${styles.entityTypeBadge} ${getEntityTypeClass(selectedLog.entityType)}`}>
                    {selectedLog.entityType}
                  </span>
                  <span>ID: {selectedLog.entityId}</span>
                </Space>
              </div>
            </div>
          </div>

          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Log ID">{selectedLog.id}</Descriptions.Item>
            <Descriptions.Item label="Entity ID">{selectedLog.entityId}</Descriptions.Item>
            <Descriptions.Item label="Entity Type" span={2}>
              <span className={`${styles.entityTypeBadge} ${getEntityTypeClass(selectedLog.entityType)}`}>
                {EntityTypeIcons[selectedLog.entityType] || EntityTypeIcons.default}
                <span style={{ marginLeft: 4 }}>{selectedLog.entityType}</span>
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Deleted At" span={2}>
              {dataDeletionLogsService.formatTimestamp(selectedLog.deletedAt)}
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({dataDeletionLogsService.getRelativeTime(selectedLog.deletedAt)})
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Deleted By" span={2}>
              {selectedLog.deletedByUserEmail ? (
                <Space>
                  <Avatar size="small" icon={<UserOutlined />} />
                  <span>{selectedLog.deletedByUserDisplayName || selectedLog.deletedByUserEmail}</span>
                  <Text type="secondary">({selectedLog.deletedByUserEmail})</Text>
                </Space>
              ) : (
                <Text type="secondary">System</Text>
              )}
            </Descriptions.Item>
            {selectedLog.deletionReason && (
              <Descriptions.Item label="Reason" span={2}>
                {selectedLog.deletionReason}
              </Descriptions.Item>
            )}
            {selectedLog.correlationId && (
              <Descriptions.Item label="Correlation ID" span={2}>
                <Tag>{selectedLog.correlationId}</Tag>
              </Descriptions.Item>
            )}
            {selectedLog.clientIpAddress && (
              <Descriptions.Item label="Client IP">
                {selectedLog.clientIpAddress}
              </Descriptions.Item>
            )}
          </Descriptions>

          {selectedLog.entitySnapshot && (
            <div className={styles.detailSection} style={{ marginTop: 16 }}>
              <Title level={5}>
                <DatabaseOutlined style={{ marginRight: 8 }} />
                {t('admin.deletionLogs.detail.snapshot', 'Entity Snapshot')}
              </Title>
              <Alert
                message="Sensitive data excluded"
                type="info"
                showIcon
                style={{ marginBottom: 8 }}
              />
              <pre className={styles.snapshotBox}>
                {formatJson(selectedLog.entitySnapshot)}
              </pre>
            </div>
          )}

          {selectedLog.metadata && (
            <div className={styles.detailSection}>
              <Title level={5}>{t('admin.deletionLogs.detail.metadata', 'Metadata')}</Title>
              <pre className={styles.metadataBox}>
                {formatJson(selectedLog.metadata)}
              </pre>
            </div>
          )}
        </div>
      )}
    </Modal>
  );

  /**
   * Render cleanup modal
   */
  const renderCleanupModal = () => (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
          {t('admin.deletionLogs.cleanup.title', 'Cleanup Old Logs')}
        </Space>
      }
      open={cleanupModalVisible}
      onCancel={() => {
        setCleanupModalVisible(false);
        setCleanupDate(null);
        setCleanupEntityType(null);
      }}
      footer={null}
      width={500}
    >
      <Spin spinning={cleanupLoading}>
        <Alert
          message={t('admin.deletionLogs.cleanup.warning', 'This action cannot be undone')}
          description="Deleted audit logs cannot be recovered. Make sure to export important logs before cleanup."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <div style={{ marginBottom: 16 }}>
          <div className={styles.filterLabel}>
            Delete logs older than:
          </div>
          <DatePicker
            style={{ width: '100%' }}
            value={cleanupDate}
            onChange={setCleanupDate}
            disabledDate={(current) => current && current > dayjs().endOf('day')}
          />
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <div className={styles.filterLabel}>
            Entity Type (optional):
          </div>
          <Select
            style={{ width: '100%' }}
            placeholder="All Types"
            allowClear
            value={cleanupEntityType}
            onChange={setCleanupEntityType}
          >
            {entityTypes.map(type => (
              <Select.Option key={type} value={type}>
                <Space>
                  {EntityTypeIcons[type] || EntityTypeIcons.default}
                  {type}
                </Space>
              </Select.Option>
            ))}
          </Select>
        </div>
        
        <Popconfirm
          title="Are you sure you want to delete these logs?"
          onConfirm={handleCleanup}
          okText="Yes, delete"
          okButtonProps={{ danger: true }}
        >
          <Button danger block icon={<DeleteOutlined />}>
            Cleanup Logs
          </Button>
        </Popconfirm>
      </Spin>
    </Modal>
  );

  return (
    <PagesWrapper>
      <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.titleSection}>
              <AuditOutlined className={styles.titleIcon} />
              <Title level={2} className={styles.title}>
                {t('admin.deletionLogs.title', 'Data Deletion Logs')}
              </Title>
            </div>
            <Text type="secondary">
              {t('admin.deletionLogs.description', 'View audit trail of all deleted data across the system for compliance and recovery')}
            </Text>
          </div>

          {/* Statistics */}
          {renderStatistics()}

          {/* Entity Distribution */}
          {renderEntityDistribution()}

          {/* Toolbar */}
          <div className={styles.toolbar}>
            <Space>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowFilters(!showFilters)}
                type={showFilters ? 'primary' : 'default'}
              >
                {t('common.filters', 'Filters')}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  fetchLogs(pagination.current, pagination.pageSize);
                  fetchStats();
                }}
              >
                {t('common.refresh', 'Refresh')}
              </Button>
            </Space>
            <Space>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
              >
                {t('admin.deletionLogs.export', 'Export')}
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => setCleanupModalVisible(true)}
              >
                {t('admin.deletionLogs.cleanup.button', 'Cleanup')}
              </Button>
            </Space>
          </div>

          {/* Filters */}
          {showFilters && renderFilterPanel()}

          {/* Table */}
          <Table
            columns={columns}
            dataSource={logs}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100', '200'],
              showTotal: (total, range) =>
                t('common.pagination', '{{start}}-{{end}} of {{total}} items', {
                  start: range[0],
                  end: range[1],
                  total,
                }),
            }}
            onChange={handleTableChange}
            scroll={{ x: 1000 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('admin.deletionLogs.noLogs', 'No deletion logs found')}
                />
              ),
            }}
          />

        {/* Modals */}
        {renderDetailModal()}
        {renderCleanupModal()}
      </div>
    </PagesWrapper>
  );
};

export default DataDeletionLogs;
